import React, { useState } from 'react';
import { FiSearch, FiInfo, FiRefreshCw, FiCheck, FiCheckCircle, FiX, FiDownload, FiUserX, FiTrash2, FiSend } from 'react-icons/fi';
import { useApplications } from '../hooks/useApplications';
import { useSettings } from '../hooks/useSettings';
import { useRejectionReasons } from '../hooks/useRejectionReasons';
import { useApplicationExport } from '../hooks/useApplicationExport';
import { useGroups } from '../hooks/useGroups';
import '../styles/applications.scss';

const STATUS_OPTIONS = [
    { value: '', label: 'الكل' },
    { value: 'pending', label: 'معلق' },
    { value: 'accepted', label: 'مقبول' },
    { value: 'rejected', label: 'مرفوض' }
];

const STATUS_LABELS = { pending: 'معلق', accepted: 'مقبول', rejected: 'مرفوض' };

const EMPTY_EXPORT_FILTERS = { city: [], referenceNumber: 'APP-', phoneNumber: '', status: '', minAge: '', maxAge: '', group: '' };

const calculateAge = (birthDate) => {
    if (!birthDate) return '-';
    const birth = new Date(birthDate);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        age--;
    }
    return age;
};

const DEFAULT_FILTER_DRAFT = { city: '', referenceNumber: 'APP-', phoneNumber: '', status: 'pending', group: '' };

export const Applications = () => {
    const {
        applications,
        isLoading,
        currentPage,
        totalPages,
        total,
        grandTotal,
        fetchApplications,
        applyFilters,
        clearFilters,
        updateApplicationStatus,
        acceptAllVisible,
        isAcceptingAll,
        unverifiedApplications,
        unverifiedCurrentPage,
        unverifiedTotalPages,
        unverifiedTotal,
        isLoadingUnverified,
        fetchUnverified,
        deleteUnverified,
        isDeletingUnverified,
        sendUnverifiedReminders,
        isSendingReminders,
        reminderProgress
    } = useApplications();
    const { content: settingsContent } = useSettings();
    const { reasons } = useRejectionReasons();
    const { isExporting, progress, exportApplications } = useApplicationExport();
    const { groups } = useGroups();
    const cities = settingsContent.cities || [];

    const [filterDraft, setFilterDraft] = useState(DEFAULT_FILTER_DRAFT);
    const [selectedApplication, setSelectedApplication] = useState(null);
    const [rejectionDraft, setRejectionDraft] = useState('');
    const [isRejecting, setIsRejecting] = useState(false);
    const [isAcceptAllConfirmOpen, setIsAcceptAllConfirmOpen] = useState(false);

    const [isUnverifiedModalOpen, setIsUnverifiedModalOpen] = useState(false);
    const [unverifiedDeleteTarget, setUnverifiedDeleteTarget] = useState(null);
    const [isSendRemindersConfirmOpen, setIsSendRemindersConfirmOpen] = useState(false);

    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [exportFilters, setExportFilters] = useState(EMPTY_EXPORT_FILTERS);
    const [exportFormat, setExportFormat] = useState('excel');
    const [exportFileCount, setExportFileCount] = useState(1);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilterDraft((prev) => ({ ...prev, [name]: value }));
    };

    const handleApplyFilters = (e) => {
        e.preventDefault();
        applyFilters(filterDraft);
    };

    const handleClearFilters = () => {
        setFilterDraft(DEFAULT_FILTER_DRAFT);
        clearFilters();
    };

    const openDetails = (application) => {
        setSelectedApplication(application);
        setRejectionDraft(application.rejectionReason || '');
        setIsRejecting(false);
    };

    const closeDetails = () => {
        setSelectedApplication(null);
        setRejectionDraft('');
        setIsRejecting(false);
    };

    const handleAccept = async () => {
        const updated = await updateApplicationStatus(selectedApplication._id, { status: 'accepted' });
        if (updated) closeDetails();
    };

    const handleConfirmReject = async () => {
        if (!rejectionDraft.trim()) return;
        const updated = await updateApplicationStatus(selectedApplication._id, {
            status: 'rejected',
            rejectionReason: rejectionDraft
        });
        if (updated) closeDetails();
    };

    const openExportModal = () => {
        setExportFilters(EMPTY_EXPORT_FILTERS);
        setExportFormat('excel');
        setExportFileCount(1);
        setIsExportModalOpen(true);
    };

    const closeExportModal = () => {
        if (isExporting) return;
        setIsExportModalOpen(false);
    };

    const handleExportFilterChange = (e) => {
        const { name, value } = e.target;
        setExportFilters((prev) => ({ ...prev, [name]: value }));
    };

    const toggleExportCity = (city) => {
        setExportFilters((prev) => ({
            ...prev,
            city: prev.city.includes(city) ? prev.city.filter((c) => c !== city) : [...prev.city, city]
        }));
    };

    const toggleAllExportCities = () => {
        setExportFilters((prev) => ({
            ...prev,
            city: prev.city.length === cities.length ? [] : [...cities]
        }));
    };

    const handleExportSubmit = async (e) => {
        e.preventDefault();
        const done = await exportApplications(exportFilters, exportFormat, exportFileCount);
        if (done) setIsExportModalOpen(false);
    };

    const pendingVisibleCount = applications?.filter((a) => a.status === 'pending').length || 0;

    const handleConfirmAcceptAll = async () => {
        await acceptAllVisible();
        setIsAcceptAllConfirmOpen(false);
    };

    const openUnverifiedModal = () => {
        setIsUnverifiedModalOpen(true);
        fetchUnverified(1);
    };

    const handleConfirmDeleteUnverified = async () => {
        const deleted = await deleteUnverified([unverifiedDeleteTarget]);
        if (deleted) setUnverifiedDeleteTarget(null);
    };

    const handleConfirmSendReminders = async () => {
        const ids = unverifiedApplications.map((a) => a._id);
        await sendUnverifiedReminders(ids);
        setIsSendRemindersConfirmOpen(false);
    };

    const isRemindersBusy = isSendingReminders || (reminderProgress && !reminderProgress.isComplete);

    return (
        <section className="applications-page">
            <div className="page-header">
                <button type="button" className="export-btn" onClick={openExportModal}>
                    <FiDownload /> استخراج
                </button>
                <button
                    type="button"
                    className="accept-all-btn"
                    onClick={() => setIsAcceptAllConfirmOpen(true)}
                    disabled={isAcceptingAll || pendingVisibleCount === 0}
                >
                    <FiCheckCircle /> {isAcceptingAll ? 'جارٍ القبول...' : 'قبول الكل'}
                </button>
                <button
                    type="button"
                    className="unverified-btn"
                    onClick={openUnverifiedModal}
                >
                    <FiUserX /> طلبات لم يتم التحقق منها
                </button>
            </div>

            <form className="filters-bar" onSubmit={handleApplyFilters}>
                <div className="filter-field">
                    <label>رقم الهاتف</label>
                    <input
                        type="text"
                        name="phoneNumber"
                        placeholder="بحث برقم الهاتف..."
                        value={filterDraft.phoneNumber}
                        onChange={handleFilterChange}
                    />
                </div>
                <div className="filter-field">
                    <label>الرقم المرجعي</label>
                    <input
                        type="text"
                        name="referenceNumber"
                        placeholder="APP-0123"
                        value={filterDraft.referenceNumber}
                        onChange={handleFilterChange}
                    />
                </div>
                <div className="filter-field">
                    <label>المدينة</label>
                    <select name="city" value={filterDraft.city} onChange={handleFilterChange}>
                        <option value="">الكل</option>
                        {cities.map((city) => (
                            <option key={city} value={city}>{city}</option>
                        ))}
                    </select>
                </div>
                <div className="filter-field">
                    <label>الحالة</label>
                    <select name="status" value={filterDraft.status} onChange={handleFilterChange}>
                        {STATUS_OPTIONS.map(({ value, label }) => (
                            <option key={value} value={value}>{label}</option>
                        ))}
                    </select>
                </div>
                <div className="filter-field">
                    <label>المجموعة</label>
                    <select name="group" value={filterDraft.group} onChange={handleFilterChange}>
                        <option value="">الكل</option>
                        <option value="none">بدون مجموعة</option>
                        {groups.map((group) => (
                            <option key={group._id} value={group._id}>المجموعة {group.number}</option>
                        ))}
                    </select>
                </div>
                <div className="filter-actions">
                    <button type="submit" className="apply-btn">
                        <FiSearch /> تطبيق
                    </button>
                    <button type="button" className="clear-btn" onClick={handleClearFilters}>
                        <FiRefreshCw /> إعادة تعيين
                    </button>
                </div>
            </form>

            {!isLoading && (
                <div className="results-count">
                    عدد النتائج: <strong>{total}</strong> من إجمالي <strong>{grandTotal}</strong> طلب
                </div>
            )}

            {isLoading ? (
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                </div>
            ) : (
                <>
                    <div className="applications-table">
                        <table>
                            <thead>
                                <tr>
                                    <th>الحالة</th>
                                    <th>الاسم الكامل</th>
                                    <th>الرقم الوطني</th>
                                    <th>رقم الهاتف</th>
                                    <th>المدينة</th>
                                    <th>العمر</th>
                                    <th>الرقم المرجعي</th>
                                    <th>المجموعة</th>
                                    <th>تاريخ التقديم</th>
                                    <th>الإجراءات</th>
                                </tr>
                            </thead>
                            <tbody>
                                {applications?.map((application) => (
                                    <tr key={application._id}>
                                        <td>
                                            <span
                                                className={`status-dot ${application.status}`}
                                                title={STATUS_LABELS[application.status] || application.status}
                                            ></span>
                                        </td>
                                        <td>{application.fullName}</td>
                                        <td>{application.nationalNumber}</td>
                                        <td>{application.phoneNumber}</td>
                                        <td>{application.city}</td>
                                        <td>{calculateAge(application.birthDate)}</td>
                                        <td>{application.referenceNumber}</td>
                                        <td>{application.group ? `المجموعة ${application.group.number}` : '-'}</td>
                                        <td>{new Date(application.submittedAt).toLocaleDateString('ar-LY')}</td>
                                        <td>
                                            <button
                                                className="info-btn"
                                                onClick={() => openDetails(application)}
                                            >
                                                <FiInfo /> التفاصيل
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {applications?.length === 0 && (
                            <div className="no-results">لا توجد طلبات مطابقة</div>
                        )}
                    </div>

                    {totalPages > 1 && (
                        <div className="pagination">
                            <button
                                onClick={() => fetchApplications(currentPage - 1)}
                                disabled={currentPage === 1}
                            >
                                السابق
                            </button>
                            {[...Array(totalPages)].map((_, i) => (
                                <button
                                    key={i + 1}
                                    onClick={() => fetchApplications(i + 1)}
                                    className={currentPage === i + 1 ? 'active' : ''}
                                >
                                    {i + 1}
                                </button>
                            ))}
                            <button
                                onClick={() => fetchApplications(currentPage + 1)}
                                disabled={currentPage === totalPages}
                            >
                                التالي
                            </button>
                        </div>
                    )}
                </>
            )}

            {selectedApplication && (
                <div className="modal">
                    <div className="modal-content application-info-modal">
                        <h2>تفاصيل الطلب</h2>

                        {selectedApplication.editedTimes > 0 && (
                            <div className="resubmission-notice">
                                تم إعادة تقديم هذا الطلب بعد الرفض.
                                {selectedApplication.rejectionReason && (
                                    <> سبب الرفض السابق: <strong>{selectedApplication.rejectionReason}</strong></>
                                )}
                            </div>
                        )}

                        <div className="info-group">
                            <p>
                                <span>الحالة:</span>
                                <span className={`status-dot ${selectedApplication.status}`}></span>
                                {STATUS_LABELS[selectedApplication.status] || selectedApplication.status}
                            </p>
                            <p><span>الاسم الكامل:</span> {selectedApplication.fullName}</p>
                            <p><span>الرقم الوطني:</span> {selectedApplication.nationalNumber}</p>
                            <p><span>رقم الهاتف:</span> {selectedApplication.phoneNumber}</p>
                            <p><span>المدينة:</span> {selectedApplication.city}</p>
                            <p><span>تاريخ الميلاد:</span> {new Date(selectedApplication.birthDate).toLocaleDateString('ar-LY')}</p>
                            <p><span>العمر:</span> {calculateAge(selectedApplication.birthDate)}</p>
                            <p><span>الرقم المرجعي:</span> {selectedApplication.referenceNumber}</p>
                            <p><span>تاريخ التقديم:</span> {new Date(selectedApplication.submittedAt).toLocaleDateString('ar-LY')}</p>
                            {selectedApplication.status === 'rejected' && selectedApplication.editedTimes === 0 && (
                                <p><span>سبب الرفض:</span> {selectedApplication.rejectionReason}</p>
                            )}
                        </div>

                        <div className="decision-section">
                            {!isRejecting ? (
                                <div className="decision-actions">
                                    <button type="button" className="accept-btn" onClick={handleAccept}>
                                        <FiCheck /> قبول
                                    </button>
                                    <button type="button" className="reject-btn" onClick={() => setIsRejecting(true)}>
                                        <FiX /> رفض
                                    </button>
                                </div>
                            ) : (
                                <div className="rejection-form">
                                    <label>سبب الرفض</label>
                                    <input
                                        type="text"
                                        list="rejection-reasons-list"
                                        value={rejectionDraft}
                                        onChange={(e) => setRejectionDraft(e.target.value)}
                                        placeholder="اكتب سبب الرفض أو اختر من القائمة..."
                                    />
                                    <datalist id="rejection-reasons-list">
                                        {reasons.map((reason) => (
                                            <option key={reason} value={reason} />
                                        ))}
                                    </datalist>
                                    <div className="decision-actions">
                                        <button
                                            type="button"
                                            className="reject-btn"
                                            onClick={handleConfirmReject}
                                            disabled={!rejectionDraft.trim()}
                                        >
                                            <FiX /> تأكيد الرفض
                                        </button>
                                        <button type="button" className="cancel-btn" onClick={() => setIsRejecting(false)}>
                                            إلغاء
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        <button className="close-btn" onClick={closeDetails}>
                            إغلاق
                        </button>
                    </div>
                </div>
            )}

            {isAcceptAllConfirmOpen && (
                <div className="modal">
                    <div className="modal-content accept-all-confirmation">
                        <div className="confirm-icon">
                            <FiCheckCircle />
                        </div>
                        <h2>تأكيد قبول جميع الطلبات المعلقة</h2>
                        <p>
                            سيتم قبول <strong>{pendingVisibleCount}</strong> طلب معلق من الطلبات الظاهرة حالياً في القائمة فقط.
                            أي طلب جديد تم تقديمه بعد فتح هذه الصفحة لن يتأثر.
                        </p>
                        <div className="modal-actions">
                            <button
                                type="button"
                                className="accept-btn"
                                onClick={handleConfirmAcceptAll}
                                disabled={isAcceptingAll}
                            >
                                <FiCheck /> {isAcceptingAll ? 'جارٍ القبول...' : 'تأكيد القبول'}
                            </button>
                            <button
                                type="button"
                                className="cancel-btn"
                                onClick={() => setIsAcceptAllConfirmOpen(false)}
                                disabled={isAcceptingAll}
                            >
                                إلغاء
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isUnverifiedModalOpen && (
                <div className="modal">
                    <div className="modal-content unverified-modal">
                        <h2>طلبات لم يتم التحقق من رقم هاتفها</h2>

                        {!isLoadingUnverified && (
                            <div className="unverified-total">
                                إجمالي الطلبات: <strong>{unverifiedTotal}</strong>
                                {unverifiedTotalPages > 1 && (
                                    <span> — صفحة {unverifiedCurrentPage} من {unverifiedTotalPages}</span>
                                )}
                            </div>
                        )}

                        {isLoadingUnverified ? (
                            <div className="loading-container">
                                <div className="loading-spinner"></div>
                            </div>
                        ) : unverifiedApplications.length === 0 ? (
                            <div className="no-results">لا توجد طلبات غير محققة حالياً</div>
                        ) : (
                            <div className="applications-table">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>الاسم الكامل</th>
                                            <th>رقم الهاتف</th>
                                            <th>الرقم الوطني</th>
                                            <th>المدينة</th>
                                            <th>تاريخ الطلب</th>
                                            <th>الإجراءات</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {unverifiedApplications.map((application) => (
                                            <tr key={application._id}>
                                                <td>{application.fullName || '-'}</td>
                                                <td>{application.phoneNumber}</td>
                                                <td>{application.nationalNumber || '-'}</td>
                                                <td>{application.city || '-'}</td>
                                                <td>{new Date(application.createdAt).toLocaleString('ar-LY')}</td>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="delete-btn"
                                                        onClick={() => setUnverifiedDeleteTarget(application._id)}
                                                        disabled={isRemindersBusy}
                                                    >
                                                        <FiTrash2 /> حذف
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {unverifiedTotalPages > 1 && (
                            <div className="pagination">
                                <button
                                    onClick={() => fetchUnverified(unverifiedCurrentPage - 1)}
                                    disabled={unverifiedCurrentPage === 1 || isLoadingUnverified || isRemindersBusy}
                                >
                                    السابق
                                </button>
                                <span className="pagination-info">{unverifiedCurrentPage} / {unverifiedTotalPages}</span>
                                <button
                                    onClick={() => fetchUnverified(unverifiedCurrentPage + 1)}
                                    disabled={unverifiedCurrentPage === unverifiedTotalPages || isLoadingUnverified || isRemindersBusy}
                                >
                                    التالي
                                </button>
                            </div>
                        )}

                        {reminderProgress && (
                            <div className="send-progress">
                                <div className="send-progress__bar">
                                    <div
                                        className="send-progress__fill"
                                        style={{
                                            width: `${reminderProgress.total
                                                ? Math.min(100, Math.round((reminderProgress.sent / reminderProgress.total) * 100))
                                                : 0}%`
                                        }}
                                    />
                                </div>
                                <span>جارٍ إرسال رمز التحقق: {reminderProgress.sent} / {reminderProgress.total}</span>
                            </div>
                        )}

                        <div className="modal-actions">
                            <button
                                type="button"
                                className="accept-btn"
                                onClick={() => setIsSendRemindersConfirmOpen(true)}
                                disabled={unverifiedApplications.length === 0 || isRemindersBusy}
                            >
                                <FiSend /> {isRemindersBusy ? 'جارٍ الإرسال...' : `إرسال رمز التحقق (${unverifiedApplications.length})`}
                            </button>
                            <button type="button" className="close-btn" onClick={() => setIsUnverifiedModalOpen(false)} disabled={isRemindersBusy}>
                                إغلاق
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isSendRemindersConfirmOpen && (
                <div className="modal">
                    <div className="modal-content accept-all-confirmation">
                        <div className="confirm-icon">
                            <FiSend />
                        </div>
                        <h2>تأكيد إرسال رمز التحقق</h2>
                        <p>
                            سيتم إرسال رمز التحقق الخاص بكل متقدم مع رابط الموقع إلى <strong>{unverifiedApplications.length}</strong> رقم هاتف من الطلبات الظاهرة حالياً في هذه الصفحة فقط
                            {unverifiedTotalPages > 1 && ' (استخدم أزرار الصفحات للانتقال وإرسال الرسالة لبقية الطلبات)'}.
                        </p>
                        <div className="modal-actions">
                            <button
                                type="button"
                                className="accept-btn"
                                onClick={handleConfirmSendReminders}
                                disabled={isRemindersBusy}
                            >
                                <FiCheck /> {isRemindersBusy ? 'جارٍ الإرسال...' : 'تأكيد'}
                            </button>
                            <button
                                type="button"
                                className="cancel-btn"
                                onClick={() => setIsSendRemindersConfirmOpen(false)}
                                disabled={isRemindersBusy}
                            >
                                إلغاء
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {unverifiedDeleteTarget && (
                <div className="modal">
                    <div className="modal-content accept-all-confirmation danger">
                        <div className="confirm-icon">
                            <FiTrash2 />
                        </div>
                        <h2>تأكيد الحذف</h2>
                        <p>سيتم حذف هذا الطلب بشكل نهائي، هل أنت متأكد؟</p>
                        <div className="modal-actions">
                            <button
                                type="button"
                                className="delete-btn"
                                onClick={handleConfirmDeleteUnverified}
                                disabled={isDeletingUnverified}
                            >
                                <FiTrash2 /> {isDeletingUnverified ? 'جارٍ الحذف...' : 'تأكيد الحذف'}
                            </button>
                            <button
                                type="button"
                                className="cancel-btn"
                                onClick={() => setUnverifiedDeleteTarget(null)}
                                disabled={isDeletingUnverified}
                            >
                                إلغاء
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isExportModalOpen && (
                <div className="modal">
                    <div className="modal-content export-modal">
                        <h2>استخراج الطلبات</h2>

                        <form onSubmit={handleExportSubmit}>
                            <div className="filters-grid">
                                <div className="filter-field">
                                    <label>رقم الهاتف</label>
                                    <input
                                        type="text"
                                        name="phoneNumber"
                                        placeholder="بحث برقم الهاتف..."
                                        value={exportFilters.phoneNumber}
                                        onChange={handleExportFilterChange}
                                        disabled={isExporting}
                                    />
                                </div>
                                <div className="filter-field">
                                    <label>الرقم المرجعي</label>
                                    <input
                                        type="text"
                                        name="referenceNumber"
                                        placeholder="APP-0123"
                                        value={exportFilters.referenceNumber}
                                        onChange={handleExportFilterChange}
                                        disabled={isExporting}
                                    />
                                </div>
                                <div className="filter-field">
                                    <label>الحالة</label>
                                    <select
                                        name="status"
                                        value={exportFilters.status}
                                        onChange={handleExportFilterChange}
                                        disabled={isExporting}
                                    >
                                        {STATUS_OPTIONS.map(({ value, label }) => (
                                            <option key={value} value={value}>{label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="filter-field">
                                    <label>العمر من</label>
                                    <input
                                        type="number"
                                        name="minAge"
                                        min="0"
                                        value={exportFilters.minAge}
                                        onChange={handleExportFilterChange}
                                        disabled={isExporting}
                                    />
                                </div>
                                <div className="filter-field">
                                    <label>العمر إلى</label>
                                    <input
                                        type="number"
                                        name="maxAge"
                                        min="0"
                                        value={exportFilters.maxAge}
                                        onChange={handleExportFilterChange}
                                        disabled={isExporting}
                                    />
                                </div>
                            </div>

                            <div className="filter-field">
                                <div className="cities-header">
                                    <label>المدن</label>
                                    <button
                                        type="button"
                                        className="toggle-all-btn"
                                        onClick={toggleAllExportCities}
                                        disabled={isExporting}
                                    >
                                        {exportFilters.city.length === cities.length ? 'إلغاء تحديد الكل' : 'تحديد الكل'}
                                    </button>
                                </div>
                                <div className="cities-checklist">
                                    {cities.map((city) => (
                                        <label key={city} className="city-checkbox">
                                            <input
                                                type="checkbox"
                                                checked={exportFilters.city.includes(city)}
                                                onChange={() => toggleExportCity(city)}
                                                disabled={isExporting}
                                            />
                                            {city}
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div className="export-options">
                                <div className="filter-field">
                                    <label>صيغة الملف</label>
                                    <div className="format-toggle">
                                        <button
                                            type="button"
                                            className={exportFormat === 'excel' ? 'active' : ''}
                                            onClick={() => setExportFormat('excel')}
                                            disabled={isExporting}
                                        >
                                            Excel
                                        </button>
                                        <button
                                            type="button"
                                            className={exportFormat === 'pdf' ? 'active' : ''}
                                            onClick={() => setExportFormat('pdf')}
                                            disabled={isExporting}
                                        >
                                            PDF
                                        </button>
                                    </div>
                                </div>

                                <div className="filter-field">
                                    <label>عدد الملفات</label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={exportFileCount}
                                        onChange={(e) => setExportFileCount(Math.max(1, Number(e.target.value) || 1))}
                                        disabled={isExporting}
                                    />
                                </div>
                            </div>
                            <p className="hint">
                                سيتم تقسيم الطلبات (مرتبة حسب الرقم المرجعي) بالتساوي على عدد الملفات المحدد
                            </p>

                            {isExporting && progress.total > 0 && (
                                <div className="export-progress">
                                    <div className="export-progress__bar">
                                        <div
                                            className="export-progress__fill"
                                            style={{ width: `${Math.min(100, Math.round((progress.done / progress.total) * 100))}%` }}
                                        />
                                    </div>
                                    <span>جارٍ إنشاء الملف: {progress.done} / {progress.total}</span>
                                </div>
                            )}

                            <div className="modal-actions">
                                <button type="submit" className="apply-btn" disabled={isExporting}>
                                    {isExporting ? 'جارٍ الاستخراج...' : 'استخراج'}
                                </button>
                                <button type="button" className="clear-btn" onClick={closeExportModal} disabled={isExporting}>
                                    إلغاء
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </section>
    );
};

export default Applications;
